import { LanguageSupport, StreamLanguage } from "@codemirror/language"
import { AppNode } from "@features/builder/graph/types"
import { expressionCompletion } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/expressionCompletion"
import { expressionLinter } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/expressionLinter"
import { expressionTheme } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/expressionTheme"
import { bracketSelectionMatching } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/bracketMatching/bracketSelectionMatching"
import { bracketHoverMatching } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/bracketMatching/bracketHoverMatching"
import { nodesMapper } from "@features/builder/helpers/nodesMapper"
import { EXPRESSION_RESERVED_KEYWORDS } from "@utils/node/keywords"

export const expressionLanguage = (node: AppNode, nodes: AppNode[]) => {
  const mapper = nodesMapper(nodes)

  const lang = StreamLanguage.define({
    name: "expression",
    startState: () => ({ inNumber: false }),
    token: (stream, _state) => {
      // Skip whitespace
      if (stream.eatSpace()) return null

      // Numbers (integers and floats)
      if (stream.match(/^\d+\.?\d*/)) return "number"

      // Operators
      if (stream.match(/^(\*\*|\/\/|==|!=|<=|>=|and|or|not)/)) return "operator"
      if (stream.match(/^[+\-*/<>!&|]/)) return "operator"

      // Math functions
      const fns = EXPRESSION_RESERVED_KEYWORDS.join("|")
      if (stream.match(new RegExp(`^(${fns})(?=\\()`))) {
        return "propertyName"
      }

      // .loc special syntax from pandas .loc['x']['y']
      if (stream.match(/^\.loc/)) return "propertyName"

      // Keywords
      if (stream.match(/^(in)\b/)) return "keyword"

      // Parentheses
      if (stream.match(/^[()]/)) return "paren"

      // Brackets
      if (stream.match(/^[\[\]]/)) return "bracket"

      // Braces
      if (stream.match(/^[{}]/)) return "brace"

      // Variable names (letters, numbers, underscore)
      if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) return "variable"

      // Comma
      if (stream.match(/^,/)) return "punctuation"

      // Default: consume one character
      stream.next()
      return null
    },
  })

  return new LanguageSupport(lang, [
    ...expressionTheme,
    expressionCompletion(node, mapper),
    expressionLinter(node, mapper),
    bracketSelectionMatching(),
    bracketHoverMatching(),
  ])
}
